import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';

import { AutoCompleteDropdownComponent } from '../../../shared/controls/auto-complete-dropdown/auto-complete-dropdown.component';

import { ToastService } from '../../../shared/toast/toast.service';
import { ErrorService } from '../../../shared/services/error-service';

import { ClientService, CountryService, CreateClientInput, CreateResult, UpdateClientInput} from '../../../../service/proxy';

@Component({
    selector: 'client-detail',
    templateUrl: './client-detail.component.html',
    styleUrls: ['./client-detail.component.scss'],

    imports: [
        CommonModule,
        FormsModule,
        AutoCompleteDropdownComponent,
    ],

    providers: [
        ClientService,
        CountryService,
    ]
})
export class ClientDetailComponent implements OnInit {
    @ViewChild('form')
    form: NgForm | undefined;

    public clientId: number = 0;
    public client: any = {};

    public showEdit: boolean = false;
    public showDelete: boolean = false;

    public selectedCountryId: number | undefined = undefined;
    public countries: { id: number; name: string; }[] = [];

    public isSaving: boolean = false;
    public formSubmitted: boolean = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private toastService: ToastService,
        private errorService: ErrorService,
        private clientService: ClientService,
        private countryService: CountryService) {}

    ngOnInit(): void {
        this.route.paramMap.subscribe(params => {
            this.clientId = Number(params.get('id'));

            if (this.clientId > 0) {
                this.getClientById(this.clientId);
            }
            else {
                this.getCountriesForLookup();
            }
        });
    }

    getClientById(id: number): void {
        this.clientService.getClientById(id)
            .subscribe({
                next: (result: any) => {
                    this.client = result;
                    this.selectedCountryId = result.country;
                    this.getCountriesForLookup();
                },
                error: (error: any) => {
                    this.errorService.handleError(error);
                }
            }
        );
    }

    getCountriesForLookup(): void {
        this.countryService.getAllCountries()
            .subscribe({
                next: (response) => {
                    this.countries = response.results.map(country => ({
                        id: country.id,
                        name: country.countryName
                    }));
                },
                error: (error: any) => {
                    this.errorService.handleError(error);
                }
            }
        );
    }

    onCountrySelectionChange(selectedId: number | null): void {
        this.selectedCountryId = selectedId ?? undefined;
        if (this.client) {
            this.client.countryId = selectedId;
        }
    }

    onSave(form: any): void {
        this.formSubmitted = true;

        if (!form.valid || !this.selectedCountryId) {
            return;
        }

        this.isSaving = true;

        if (this.clientId === 0) {
            this.createClient();
        }
        else {
            this.updateClient();
        }
    }

    createClient() {
        const input = {
            ...this.client,
        } as CreateClientInput;

        if (this.selectedCountryId) {
            input.countryId = this.selectedCountryId;
        }

        this.clientService.createClient(input)
            .subscribe({
                next: (result: CreateResult) => {
                    this.router.navigate([`/client/` + result.id]);
                },
                error: (error: any) => {
                    this.errorService.handleError(error);
                },
                complete: () => {
                    this.isSaving = false;
                    this.toastService.show('Client created successfully.', 'success');
                }
            }
        );
    }

    updateClient() {
        const input = {
            ...this.client,
            countryId: this.selectedCountryId
        } as UpdateClientInput;

        if (this.selectedCountryId) {
            input.countryId = this.selectedCountryId;
        }

        this.clientService.updateClient(this.clientId, input)
            .subscribe({
                next: (result: any) => {
                    this.isSaving = false;
                    this.toastService.show('Client updated successfully.', 'success');

                    setTimeout(() => {
                        this.router.navigate([`/client`]);
                    }, 2000);
                },
                error: (error: any) => {
                    this.errorService.handleError(error);
                }
            }
        );
    }

    displayCountry(option: any): string {
        return option?.name || '';
    }

    onClear(): void {
        if (this.clientId > 0) {
            this.getClientById(this.clientId);
        }
        else {
            this.getCountriesForLookup();
            this.selectedCountryId = undefined;

            if (this.form) {
                this.form.resetForm();
            }
        }
    }

    onBack(): void {
        this.router.navigate(['/client']);
    }
}